/**
 * Eén prijsaanvraag per ontwerpwijziging (Ruling P6).
 *
 * Met de Vinyl Lettering Designer prijzen twee partijen hetzelfde ontwerp: de
 * printen-host (voor het prijsvak en de bestelknop in de designer) en de
 * configurator (voor `isPriceReady`, de quote-hash en de winkelwagenregel).
 * Beide posten precies dezelfde body naar `/api/pricing/calculate`: de host
 * bouwt zijn configuratie op die van de configurator (`getBaseConfiguration`)
 * en de configurator neemt maat, aantal en materiaal van de host over.
 *
 * Deze fetch deelt zo'n aanvraag: wie als tweede komt (ook terwijl de eerste
 * nog loopt) krijgt een kloon van hetzelfde antwoord. Zo verwerkt de
 * configurator het antwoord via zijn eigen, ongewijzigde prijspad, en zijn
 * quote-hash is per definitie die van de designerprijs. Volgorde maakt niet
 * uit: de host vraagt meteen, de configurator 180 ms later (debounce).
 *
 * - Sleutel = adres + body met gesorteerde sleutels (volgorde van opties telt niet).
 * - Een netwerkfout wordt niet bewaard. Een geslaagd antwoord `ttlMs` (30 s); een
 *   foutantwoord (429/500/422) alleen `errorTtlMs` (2 s): genoeg voor de debounce
 *   van de configurator, maar "opnieuw proberen" en de wachttijd van een 429 gaan
 *   echt weer naar de server.
 * - Een AbortSignal van een van beide wordt niet doorgegeven: de ander wacht er
 *   misschien nog op.
 */

import { stableJson } from './stable-json';

const PRIJS_URL = '/api/pricing/calculate';

/** Stabiele sleutel van een configuratie (volgorde van opties telt niet), bv. voor "hoort deze quote bij deze configuratie". */
export function stableConfigurationKey(configuration: Record<string, unknown>): string {
  return stableJson(configuration ?? {});
}

function sleutelVan(input: RequestInfo | URL, init?: RequestInit): string | null {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  let pad = url;
  try {
    pad = new URL(url, 'https://printen.invalid').pathname;
  } catch {
    return null;
  }
  if (pad !== PRIJS_URL) return null;
  if (String(init?.method ?? 'GET').toUpperCase() !== 'POST' || typeof init?.body !== 'string') return null;
  try {
    return pad + ' ' + stableJson(JSON.parse(init.body));
  } catch {
    return null;
  }
}

export function createSharedPriceFetch(opts: {
  fetchImpl?: typeof fetch;
  ttlMs?: number;
  errorTtlMs?: number;
  now?: () => number;
} = {}): typeof fetch {
  const doFetch = opts.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));
  const ttl = opts.ttlMs ?? 30_000;
  const foutTtl = opts.errorTtlMs ?? 2_000;
  const nu = opts.now ?? (() => Date.now());
  const bewaard = new Map<string, { antwoord: Promise<Response>; tot: number }>();

  const gedeeld = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const sleutel = sleutelVan(input, init);
    if (!sleutel) return doFetch(input, init);

    for (const [k, v] of bewaard) if (v.tot <= nu()) bewaard.delete(k);
    let regel = bewaard.get(sleutel);
    if (!regel) {
      const zonderSignal: RequestInit = { ...(init ?? {}) };
      delete zonderSignal.signal;
      const antwoord = doFetch(input, zonderSignal);
      regel = { antwoord, tot: Number.POSITIVE_INFINITY };
      bewaard.set(sleutel, regel);
      const deze = regel;
      antwoord.then(
        (res) => {
          deze.tot = nu() + (res.ok ? ttl : foutTtl);
        },
        () => {
          if (bewaard.get(sleutel) === deze) bewaard.delete(sleutel);
        }
      );
    }
    // Elke lezer een eigen kloon; het origineel wordt nooit gelezen.
    return (await regel.antwoord).clone();
  };
  return gedeeld as typeof fetch;
}
